<script lang="ts">
	import { Loader } from '@googlemaps/js-api-loader';

	type Props = {
		apiKey: string;
		id: string;
		name: string;
		placeholder?: string;
		value?: string;
		placeId?: string;
		focus?: boolean;
		setValue: (v: string) => void;
		setPlaceId: (v: string) => void;
	};

	let {
		apiKey,
		id,
		name,
		placeholder = undefined,
		focus = false,
		value = '',
		placeId = '',
		setValue,
		setPlaceId
	}: Props = $props();

	let autocomplete = $state<google.maps.places.Autocomplete>();
	let autocompleteRef = $state<HTMLInputElement>();

	const initMap = async () => {
		try {
			const loader = new Loader({
				apiKey,
				version: 'weekly',
				language: 'en',
				libraries: ['places']
			});

			const { Autocomplete } = await loader.importLibrary('places');
			const input = document.getElementById(id) as HTMLInputElement;
			const bounds = new google.maps.LatLngBounds(
				new google.maps.LatLng(36.77917, -5.24724),
				new google.maps.LatLng(57.227758, 28.031235)
			);

			autocomplete = new Autocomplete(input, {
				bounds,
				fields: ['formatted_address', 'place_id'], // Added place_id to fields
				strictBounds: false
			});

			autocomplete.addListener('place_changed', () => {
				if (!autocomplete) return;

				const place = autocomplete.getPlace();
				if (!place) return;

				const autocompletePlaceId = place.place_id;
				if (!autocompletePlaceId) return;

				// Use the input value directly rather than formatted_address
				const inputValue = input.value;
				if (!inputValue) return;

				setValue(inputValue);
				setPlaceId(autocompletePlaceId);
			});
		} catch (error) {
			console.error('Google Places Autocomplete initialization error:', error);
		}
	};

	$effect(() => {
		if (!autocomplete) {
			initMap();
		}

		if (focus) {
			autocompleteRef?.focus();
		}
	});

	const resetAutocomplete = () => {
		setPlaceId('');
		setValue('');
	};

	$effect(() => {
		if (autocompleteRef) {
			autocompleteRef.addEventListener('focus', resetAutocomplete);
		}
	});
</script>

<input
	{id}
	bind:this={autocompleteRef}
	type="search"
	{name}
	{placeholder}
	{value}
	class="w-full"
	autocomplete="off"
	autocorrect="off"
	autocapitalize="off"
	spellcheck="false"
	aria-autocomplete="list"
	aria-controls="combo-listbox"
	aria-expanded="false"
	aria-haspopup="listbox"
	role="combobox"
	onkeydown={(e) => {
		if (e.key === 'Enter') {
			e.preventDefault();
		}
	}}
/>
<input id={`${id}-place-id`} type="hidden" name={`${name}PlaceId`} value={placeId} />

<style>
	:global(.pac-container) {
		background-color: var(--color-one);
		left: 0;
		right: 0;
		z-index: 50;
		margin-top: 0.5rem;
		border-width: 0px;
		box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
		color: var(--text-color);
	}
	:global(.pac-container:after) {
		filter: grayscale(1);
	}
	:global(.pac-icon) {
		display: none;
	}
	:global(.pac-item) {
		font-family: 'Outfit Variable', system-ui;
		border-color: var(--color-two);
		border-width: 0px;
		border-bottom-width: 1px;
		padding: 0.5rem 1rem;
		font-size: 0.875rem;
		color: var(--text-color);
	}
	:global(.pac-item:hover) {
		background-color: var(--color-two);
		cursor: pointer;
	}
	:global(.pac-item-selected) {
		background-color: var(--color-two);
		color: var(--text-color);
	}
	:global(.pac-item-query) {
		display: block;
		white-space: pre-wrap;
		font-size: 1rem;
		color: var(--text-color);
	}
	:global(.pac-matched) {
		color: var(--color-primary);
		font-weight: normal;
	}
</style>
